import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Navigation, GlobalApiBridge } from '@zds3/zds-components';
import { HistoryObserver } from 'browser-history-observer';
import { userPilotReload } from '../utils/userpilotFunctions';
import { removeCookie } from '../utils/removeCookie';
import { MultiOrgCookies, WorkingOrgCookie } from '../hooks/useMultiOrgCookies';
import { useNavigationData } from '../hooks/useNavigationData';
import { NavigationData } from '../types';
import MultiOrgController from './MultiOrg/multi-org.controller';
import MultiEntityController from './MultiEntity/multi-entity.controller';
/* Deliberately NOT lazy. The search box is part of the nav chrome that
 * z-navigation renders itself, so it becomes visible whether or not this
 * controller has mounted — and on a slow connection its chunk competes for
 * bandwidth with the nav's own data requests, so it is not guaranteed to arrive
 * first. Deferring it produced a visible search field that accepted typing and
 * returned nothing. Bundle size is not worth a dead first-class control. */
import SearchController from './Search/search.controller';

/* Lazy is fine here: the support modal only opens from the help menu or the
 * `search-help` event, so a Suspense gap lands on an explicit user action —
 * which is how the legacy AppBar has always loaded its equivalent. Keeping it in
 * the entry bundle dragged html2canvas, formik, yup and most of
 * src/support-deflection into every page load. */
const LazySupportDeflection = React.lazy(
  () => import('../../support-deflection/index-new-nav'),
);
import { HelpChatController } from './HelpChat/help-chat.controller';
import { useEnvironmentBar } from './hooks/useEnvironmentBar';
import { cloneDeep } from 'lodash';
import { AmplitudeNavEvents, useAmplitude } from '../hooks/useAmplitude';
import { useVariant, Variant } from './hooks/useVariant';
import { CookiesProvider } from 'react-cookie';
import { NavigationLoadingSkeleton } from './NavigationLoadingSkeleton';
import { StaleMultiOrgCookiesCleaner } from '../hooks/useClearStaleMultiOrgCookies';
//import { useAppSwitcherData } from '../hooks/useAppSwitcherData';

if (window.customElements.get('z-navigation') === undefined) {
  window.customElements.define('z-navigation', Navigation);
}

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'z-navigation': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        children?: React.ReactNode;
        theme?: string;
      };
      div?: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
      >;
    }
  }
}

export interface NavigationProps {
  emotionMount?: HTMLDivElement;
  id?: string | null;
  isPartialOrPartialPlus?: boolean;
  data?: NavigationData;
  dataUrl?: string;
  onNavigationChanged?: (data: NavigationData) => void;
  /**
   * When true, the user has opted into the "New Zuora Experience" and the
   * Payments section shows the new Transactions menu item while hiding legacy
   * Payments/Refunds entries (ZPY-458). Sourced from `userPreferences.reinvented`
   * via `GlobalNavRoot`; passed here so nav transform can react when
   * preferences resolve after billing nav data.
   */
  isNewZuoraExperienceEnabled?: boolean;
  /** False while the Global Nav flag is still a guess — see trackGlobalNavLoaded. */
  isGlobalNavConfirmed?: boolean;
}

const observer = new HistoryObserver();

export const GlobalNav = React.memo(
  ({
    id,
    data,
    dataUrl,
    isPartialOrPartialPlus,
    onNavigationChanged,
    isNewZuoraExperienceEnabled,
    isGlobalNavConfirmed = false,
  }: NavigationProps) => {
    const { t } = useTranslation();
    const { logEvent } = useAmplitude();
    const variant = useVariant();
    const navRef = useRef<any>(null);

    const [isRendered, setIsRendered] = useState<boolean>(false);
    const [currentApp, setCurrentApp] = useState<string>('');
    const [, setAppChangeCounter] = useState(0);
    const [, setRerenderCounter] = useState(0);
    const [openSupportArticles, setOpenSupportArticles] = useState(false);
    const [searchText, setSearchText] = useState('');

    const [showGeneralFeedback, setShowGeneralFeedback] = useState(true);
    const { transformedData: rawTransformedData, error } = useNavigationData(
      isNewZuoraExperienceEnabled,
    );
    const transformedData = useMemo(() => {
      if (!rawTransformedData) {
        return null;
      }
      const clonedTransformedData = cloneDeep(rawTransformedData);
      const feedbackItem = clonedTransformedData.primary.bottom.find(
        (item) => item.icon === 'feedback',
      );
      if (feedbackItem && showGeneralFeedback) {
        feedbackItem.popover?.items?.push({
          label: t('GlobalNav.Feedback.GeneralProductFeedback'),
          id: 'Zuora/SubmitFeedback-popover-general-product-feedback',
          noIcon: true,
          expandable: false,
          customEvent: 'general-feedback',
        });
      }
      return clonedTransformedData;
    }, [rawTransformedData, showGeneralFeedback, t]);
    const { totalBarHeight } = useEnvironmentBar();

    const dynamicHeight = totalBarHeight
      ? `calc(100vh - ${totalBarHeight}px)`
      : '';
    const dynamicTop = totalBarHeight ? `${totalBarHeight}px` : '';

    const menuState = useMemo(() => {
      if (!transformedData?.secondary?.apps) {
        return { isMultiOrgEnabled: false, isMultiEntityEnabled: false };
      }

      const isMultiOrgEnabled = transformedData.secondary.apps.some(
        (app) => app.top?.multiOrgMenu,
      );
      const isMultiEntityEnabled = transformedData.secondary.apps.some(
        (app) => app.top?.multiEntityMenu,
      );

      return { isMultiOrgEnabled, isMultiEntityEnabled };
    }, [transformedData?.secondary?.apps]);

    const isBillingActive = useMemo(() => {
      return currentApp === 'billing' || currentApp === '' || !currentApp;
    }, [currentApp]);

    const toggleOpenSupportArticles = useCallback(
      (isCreateTicketOpen = false) => {
        if (!openSupportArticles) {
          // Amplitude logging logic here
        }
        if (!isCreateTicketOpen) {
          setSearchText('');
        }
        setOpenSupportArticles((prev) => !prev);
      },
      [openSupportArticles],
    );

    const toggleOpenFeedbackDialog = useCallback(() => {
      const targetContainer = document.getElementById(
        'QSIFeedbackButton-target-container',
      );
      if (targetContainer && targetContainer.style) {
        targetContainer.style.display = 'block';
      }
      const button = document.getElementById('QSIFeedbackButton-btn');
      if (button) {
        button.click();
        setShowGeneralFeedback(false);
      }
    }, []);

    const openZuoraExperienceFeedbackSurvey = useCallback(() => {
      const win = window as Window & {
        zuex_feedback_survey?: boolean;
        QSI?: { API: { unload(): void; load(): void; run(): void } };
      };
      win.zuex_feedback_survey = true;
      win.QSI?.API?.unload();
      win.QSI?.API?.load();
      win.QSI?.API?.run();
    }, []);

    const reload = useCallback((): void => {
      setTimeout(() => window.location.reload());
    }, []);

    const handleGlobalEvents = useCallback(
      (e) => {
        if (e.type === 'in-product-tutorials') {
          window.userpilot?.trigger('resource_center:hPhEnmQPb4');
        } else if (e.type === 'search-help') {
          toggleOpenSupportArticles();
        } else if (e.type === 'app-changed') {
          const appName = e.detail?.appName;
          if (appName) {
            setCurrentApp(appName);
            setAppChangeCounter((prev) => prev + 1);
          }
        } else if (e.type === 'general-feedback') {
          toggleOpenFeedbackDialog();
        } else if (e.type === 'new-zuora-experience-feedback') {
          openZuoraExperienceFeedbackSurvey();
        }
      },
      [
        toggleOpenSupportArticles,
        toggleOpenFeedbackDialog,
        openZuoraExperienceFeedbackSurvey,
      ],
    );

    /* Reports adoption once per browser, latched in localStorage — so a false
     * positive can never be corrected. Requires the resolved flag rather than
     * just being mounted: this tree also renders while the flag is still a
     * guess, and a tenant that turns out not to have Global Nav would otherwise
     * be counted, permanently, on the strength of a cached nav tree. */
    const trackGlobalNavLoaded = useCallback(() => {
      if (!isGlobalNavConfirmed) {
        return;
      }
      const storageKey = 'globalNavLoadedTracked';
      const globalNavLoaded = localStorage.getItem(storageKey);
      if (globalNavLoaded) {
        return;
      }
      localStorage.setItem(storageKey, 'true');
      const currentTenant = transformedData?.secondary?.apps
        ?.find(
          (app) =>
            (app.bottom?.length || 0) > 0 &&
            (app.top?.tenants?.length || 0) > 0,
        )
        ?.top?.tenants?.find((t) => t.isCurrent);
      logEvent(AmplitudeNavEvents.GLOBAL_NAV_LOADED, {
        variant: variant,
        tenantId: currentTenant?.tenantId || '',
        tenantName: currentTenant?.name || '',
      });
    }, [variant, transformedData, isGlobalNavConfirmed, logEvent]);

    /* Kept out of the effect that assigns navRef.current.data: confirmation
     * arrives after the data does, and re-running that effect would reassign the
     * same tree and make z-navigation rebuild itself for nothing. */
    useEffect(() => {
      if (!isRendered || isPartialOrPartialPlus || !transformedData) {
        return;
      }
      trackGlobalNavLoaded();
    }, [
      isRendered,
      isPartialOrPartialPlus,
      transformedData,
      trackGlobalNavLoaded,
    ]);

    useEffect(() => {
      if (!isRendered || !navRef.current || isPartialOrPartialPlus) {
        return;
      }

      // A language switch drops transformedData to null while the new catalog
      // loads. The nav is a web component fed imperatively, so we must clear its
      // data here — otherwise it keeps painting the previous language's nav
      // underneath the loading skeleton instead of being replaced by it.
      if (!transformedData) {
        navRef.current.data = undefined;
        return;
      }

      navRef.current.data = transformedData;

      const handleNavChange = (e) => {
        const customEvent = e;
        if (onNavigationChanged && customEvent.detail?.data) {
          onNavigationChanged(customEvent.detail.data);
        }
      };

      navRef.current.addEventListener('navigation-changed', handleNavChange);

      const handleTenantSwitch = () => {
        removeCookie(MultiOrgCookies);
        removeCookie(WorkingOrgCookie);
      };

      navRef.current.addEventListener('tenant-switch', handleTenantSwitch);

      return () => {
        if (navRef.current) {
          navRef.current.removeEventListener(
            'navigation-changed',
            handleNavChange,
          );
          navRef.current.removeEventListener(
            'tenant-switch',
            handleTenantSwitch,
          );
        }
      };
    }, [
      isRendered,
      transformedData,
      isPartialOrPartialPlus,
      onNavigationChanged,
    ]);

    useEffect(() => {
      document.addEventListener('search-help', handleGlobalEvents, true);
      document.addEventListener(
        'in-product-tutorials',
        handleGlobalEvents,
        true,
      );
      window.addEventListener('in-product-tutorials', handleGlobalEvents, true);
      window.addEventListener('app-changed', handleGlobalEvents, true);
      window.addEventListener('general-feedback', handleGlobalEvents, true);
      window.addEventListener(
        'new-zuora-experience-feedback',
        handleGlobalEvents,
        true,
      );

      return () => {
        document.removeEventListener('search-help', handleGlobalEvents, true);
        document.removeEventListener(
          'in-product-tutorials',
          handleGlobalEvents,
          true,
        );
        window.removeEventListener(
          'in-product-tutorials',
          handleGlobalEvents,
          true,
        );
        window.removeEventListener('app-changed', handleGlobalEvents, true);
        window.removeEventListener(
          'general-feedback',
          handleGlobalEvents,
          true,
        );
        window.removeEventListener(
          'new-zuora-experience-feedback',
          handleGlobalEvents,
          true,
        );
      };
    }, [handleGlobalEvents]);

    useEffect(() => {
      document.body.style.overscrollBehaviorY = 'none';

      const unsubscribe = observer.onHistoryChange((payload) => {
        if (payload?.type === 'load') {
          return;
        }
        userPilotReload();
        setRerenderCounter((prev) => prev + 1);
      });

      const timer = setTimeout(() => {
        setIsRendered(true);
      }, 0);

      return () => {
        unsubscribe();
        clearTimeout(timer);
      };
    }, []);

    const attributes = useMemo(() => {
      const attrs: Record<string, string> = {};
      if (id) attrs.id = id;
      if (dataUrl && !data) attrs['data-url'] = dataUrl;
      return attrs;
    }, [id, dataUrl, data]);

    /* Deliberately no cleanup. GlobalApiBridge exposes no unregister method and
     * keeps one private handler slot, so this overwrites rather than accumulates
     * — nothing builds up the way a subscriber list would. Clearing it on unmount
     * would be unsafe anyway: the slot cannot be read back, so with two nav
     * elements mounted the first to unmount would silently drop the other's
     * handler. And only z-navigation calls bridge.logEvent, which renders on this
     * path alone, so a handler left behind by an unmounted tree is never invoked.
     */
    useEffect(() => {
      GlobalApiBridge.getInstance().registerLogEvent(logEvent);
    }, [logEvent]);

    function findAncestorNav(el) {
      let node = el;

      while (node) {
        const nav = node.closest?.('z-unified-nav-v2');
        if (nav) return nav;

        const root = node.getRootNode();
        node = root.host || null;
      }

      return null;
    }

    const navRootElement = findAncestorNav(navRef.current);
    const showLoadingSkeleton = !transformedData && !isPartialOrPartialPlus;

    if (error && !transformedData) {
      console.error(error);
      if (isPartialOrPartialPlus) {
        return null;
      }
      return (
        <NavigationLoadingSkeleton height={dynamicHeight} top={dynamicTop} />
      );
    }

    return (
      <>
        {!isPartialOrPartialPlus && (
          <>
            <div
              aria-hidden={!showLoadingSkeleton}
              style={{
                display: showLoadingSkeleton ? 'block' : 'none',
              }}
            >
              <NavigationLoadingSkeleton
                height={dynamicHeight}
                top={dynamicTop}
              />
            </div>
            <z-navigation
              theme="blue"
              ref={navRef}
              {...attributes}
              aria-hidden={showLoadingSkeleton}
              style={{
                height: dynamicHeight,
                top: dynamicTop,
                display: showLoadingSkeleton ? 'none' : 'flex',
                pointerEvents: showLoadingSkeleton ? 'none' : 'auto',
              }}
            />
          </>
        )}

        <Suspense fallback={<React.Fragment />}>
          <LazySupportDeflection
            openSupportArticles={openSupportArticles}
            toggleOpenSupportArticles={toggleOpenSupportArticles}
            searchText={searchText}
            setSearchText={setSearchText}
          />
        </Suspense>

        <CookiesProvider>
          <HelpChatController isVisible />
          <SearchController navRootElement={navRootElement} />

          {variant === Variant.BILLING && (
            <StaleMultiOrgCookiesCleaner reloadPage={reload} />
          )}

          {variant === Variant.BILLING && menuState.isMultiOrgEnabled && (
            <MultiOrgController
              reloadPage={reload}
              menuType="org"
              isVisible={isBillingActive}
              navRootElement={navRootElement}
            />
          )}

          {variant === Variant.BILLING && menuState.isMultiEntityEnabled && (
            <MultiEntityController
              menuType="entity"
              isVisible={isBillingActive}
              navRootElement={navRootElement}
            />
          )}
        </CookiesProvider>
      </>
    );
  },
);

GlobalNav.displayName = 'GlobalNav';
